/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorService } from '@perval/shared';
import { Subject, takeUntil } from 'rxjs';
import { ModalService } from '../../shared/design-system/organisms/p-modal/p-modal.service';
import { Report } from '../models/report.model';
import { ReportService } from '../services/report.service';
import { ErrorStateService } from '../state/error-state.service';
import { AddressGeoInfo } from '../models/address-geo-info.dto';
import { AttributesService } from '../services/attributes.service';
import { EditorRoles } from '../models/editor-roles-http.dto';

enum ModalId {
  RESET_REPORT_MODAL = 'RESET_REPORT_MODAL',
  DATA_PROTECTION_MODAL = 'DATA_PROTECTION_MODAL',
}

@Component({
  selector: 'app-rapport-container',
  templateUrl: './rapport.container.html',
  styleUrls: ['./rapport.container.scss'],
})
export class RapportContainer implements OnInit, OnDestroy {
  report!: Report;

  roles!: EditorRoles;

  isLoadingResetReport = false;
  isLoadingGenerateReport = false;
  generateReportErrorMessage: string | undefined;
  modalId = ModalId;
  isResetReportModalOpen = false;
  isDataProtectionModalOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly reportService: ReportService,
    private readonly route: ActivatedRoute,
    private readonly errorStateService: ErrorStateService,
    private readonly modalService: ModalService,
    private readonly behaviorService: BehaviorService,
    private readonly attributesService: AttributesService,
  ) {}

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async init() {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (params) => {
        const reportId = params.get('rapport-id') || '';
        if (reportId) {
          this.report = await this.reportService.getReportById(reportId);
          this.roles = await this.attributesService.getReportRoles();
        }
      });

    this.modalService
      .getModalState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((modalId) => {
        this.isResetReportModalOpen = modalId === ModalId.RESET_REPORT_MODAL;
        this.isDataProtectionModalOpen =
          modalId === ModalId.DATA_PROTECTION_MODAL;
      });
  }

  async onAppraisalChanged(
    appraisalBasicInfo: Report.Appraisal.BasicInfo,
  ): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateAppraisal(
        this.report.id,
        appraisalBasicInfo,
      );
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onAppraisalEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableAppraisal(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onReferencesChanged(references: Report.References): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateReferences(
        this.report.id,
        this.report.type,
        references,
      );
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onReferencesEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableReferences(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onInformationsClientChanged(
    informationsClient: Report.Customer,
  ): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateCustomer(
        this.report.id,
        informationsClient,
      );
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onInformationsClientEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableCustomer(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onPerksChanged(perks: Report.Perk): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updatePerks(this.report.id, perks);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onPerksEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enablePerks(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onDrawbacksChanged(drawbacks: Report.Drawbacks): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateDrawbacks(this.report.id, drawbacks);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onDrawbacksEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableDrawbacks(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onCharacteristicsChanged(
    characteristics: Report.Characteristics,
  ): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateCharacteristics(
        this.report.id,
        this.report.propertyType,
        characteristics,
      );
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onCharacteristicsEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableCharacteristics(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onExpertChanged(expert: Report.Expert): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateExpert(this.report.id, expert);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onExpertEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableExpert(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onOrganismeChanged(organisme: Report.Organisme): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      if (organisme !== undefined) {
        await this.reportService.updateOrganisme(this.report.id, organisme);
      }
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onOrganismeEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableOrganisme(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onInformationsBienChanged(property: Report.Property): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateProperty(this.report.id, property);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onInformationsBienEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableProperty(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onPhotographiesChanged(
    photographies: Report.Photographs,
  ): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updatePhotographies(
        this.report.id,
        photographies,
      );
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onPhotographiesEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enablePhotographies(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onDetailsDossierChanged(detailsDossier: Report.Case): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateCase(this.report.id, detailsDossier);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(
        error?.error?.errors[0]?.message,
        false,
        error?.status,
      );
    }
  }

  async onCadastreChanged(cadastre: Report.Cadastre): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateCadastre(this.report.id, cadastre);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onAddressSaved(address: AddressGeoInfo) {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateAddress(this.report.id, address);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onDetailsDossierEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableCase(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onTendenciesEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableTendencies(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onTendenciesChanged(tendencies: Report.Tendencies): Promise<void> {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.saveTendencies(this.report.id, tendencies);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onStatisticsEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableStatistics(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onCadastreEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableCadastre(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async refreshReport(): Promise<void> {
    this.errorStateService.clearError();
    this.report = await this.reportService.getReportById(this.report.id);
  }

  async generateReport(): Promise<void> {
    try {
      this.generateReportErrorMessage = undefined;
      this.isLoadingGenerateReport = true;
      await this.reportService.generateReport(this.report.id);
      this.isLoadingGenerateReport = false;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.behaviorService.scrollToTheTop();
      this.generateReportErrorMessage =
        'Une erreur est survenue lors de la génération du rapport. Veuillez réessayer plus tard ou contacter le support si le problème persiste.';
    }
  }

  async resetReport(): Promise<void> {
    this.modalService.showModal(ModalId.RESET_REPORT_MODAL);
  }

  async onResetReportModalCancel(): Promise<void> {
    this.modalService.hideModal();
  }

  openDataProtectionModal(): void {
    this.modalService.showModal(ModalId.DATA_PROTECTION_MODAL);
  }

  onDataProtectionModalClose(): void {
    this.modalService.hideModal();
  }

  async onResetReportModalConfirm(): Promise<void> {
    this.isLoadingResetReport = true;
    this.modalService.hideModal();
    await this.reportService.resetReport(this.report.id);
    await this.refreshReport();
    this.isLoadingResetReport = false;
  }

  async onCancled(): Promise<void> {
    await this.refreshReport();
  }

  async onNotesEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableNotes(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onNotesSaved(notes: Report.Notes) {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateNotes(this.report.id, notes);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }

  async onDefinitionsEnabled(isEnabled: boolean): Promise<void> {
    await this.reportService.enableDefinitions(this.report.id, isEnabled);
    await this.refreshReport();
  }

  async onDefinitionsSaved(definitions: Report.Definitions) {
    try {
      this.isLoadingGenerateReport = true;
      await this.reportService.updateDefinitions(this.report.id, definitions);
      await this.refreshReport();
      this.modalService.hideModal();
      this.isLoadingGenerateReport = false;
    } catch (error: any) {
      this.isLoadingGenerateReport = false;
      this.errorStateService.setError(error.error.errors[0].message);
    }
  }
}
